/**
 * Immutable presentation intent for every fighter state.
 *
 * Combat owns state and timing; this module only translates that data into named visual motion.
 * Keeping the resolver three-free makes state coverage testable without constructing a renderer.
 */

import { ALL_STATES, S } from '../combat/index.js';

function profile(id, family, values = {}) {
  return Object.freeze({
    id,
    family,
    enterTicks: 8,
    recoverTicks: 22,
    intensity: 1,
    lean: 0,
    twist: 0,
    compression: 0,
    ...values,
  });
}

export const STATE_MOTION_PROFILES = Object.freeze({
  neutral: profile('neutral', 'neutral'),
  locomotion: profile('locomotion', 'locomotion', { enterTicks: 7 }),
  'locomotion-recovery': profile('locomotion-recovery', 'locomotion-recovery', {
    enterTicks: 6,
    compression: 0.08,
  }),
  sidestep: profile('sidestep', 'sidestep', { enterTicks: 5, lean: 0.12 }),
  crouch: profile('crouch', 'crouch', { enterTicks: 12, compression: 0.24 }),
  guard: profile('guard', 'guard', { enterTicks: 8 }),
  parry: profile('parry', 'parry', { enterTicks: 5, intensity: 1.08 }),
  attack: profile('attack', 'attack'),
  throw: profile('throw', 'throw'),
  'throw-break': profile('throw-break', 'throw-break', {
    recoverTicks: 22, intensity: 1.04, lean: -0.36, compression: 0.08,
  }),
  'throw-tech': profile('throw-tech', 'throw-tech', {
    recoverTicks: 16, intensity: 0.96, lean: 0.24, compression: 0.1,
  }),
  air: profile('air', 'air', { lean: -0.28 }),
  special: profile('special', 'special', { intensity: 1.12 }),
  intro: profile('intro', 'intro', { enterTicks: 18 }),

  'kestrel-hit-stand': profile('kestrel-hit-stand', 'hit-stand', {
    recoverTicks: 24, intensity: 0.94, lean: -0.38, twist: 0.14,
  }),
  'bulwark-hit-stand': profile('bulwark-hit-stand', 'hit-stand', {
    recoverTicks: 28, intensity: 0.82, lean: -0.31, twist: 0.1,
  }),
  'hit-crouch': profile('hit-crouch', 'hit-crouch', {
    recoverTicks: 26, intensity: 0.9, lean: -0.28, compression: 0.3,
  }),
  'hit-launch': profile('hit-launch', 'hit-launch', {
    recoverTicks: 36, intensity: 1.16, lean: -0.62, twist: 0.34,
  }),
  'hit-air': profile('hit-air', 'hit-air', {
    recoverTicks: 34, intensity: 1.05, lean: -0.48, twist: 0.42,
  }),
  'hit-wall': profile('hit-wall', 'hit-wall', {
    recoverTicks: 26, intensity: 1.08, lean: 0.26, compression: 0.12,
  }),
  'wall-splat': profile('wall-splat', 'wall-splat', {
    enterTicks: 4, recoverTicks: 30, intensity: 1.1, compression: 0.08,
  }),
  'wall-slump': profile('wall-slump', 'wall-slump', {
    enterTicks: 32, intensity: 0.94, compression: 0.52,
  }),
  'fall-face-up': profile('fall-face-up', 'knockdown-fall', {
    recoverTicks: 38, intensity: 1.08, lean: -1.08, twist: -0.24,
  }),
  'fall-face-down': profile('fall-face-down', 'knockdown-fall', {
    recoverTicks: 38, intensity: 1.08, lean: -1.16, twist: 0.7,
  }),
  'ground-face-up': profile('ground-face-up', 'knockdown-ground', {
    enterTicks: 6, intensity: 1, lean: 1.48, twist: -0.18,
  }),
  'ground-face-down': profile('ground-face-down', 'knockdown-ground', {
    enterTicks: 6, intensity: 1, lean: -1.5, twist: 2.76,
  }),
  'wakeup-neutral': profile('wakeup-neutral', 'wakeup-neutral', {
    enterTicks: 16, compression: 0.34,
  }),
  'wakeup-attack': profile('wakeup-attack', 'wakeup-attack', {
    enterTicks: 10, intensity: 1.08, compression: 0.28,
  }),
  'wakeup-roll': profile('wakeup-roll', 'wakeup-roll', {
    enterTicks: 28, intensity: 1.05, compression: 0.5,
  }),
  landing: profile('landing', 'landing', {
    recoverTicks: 8, compression: 0.18,
  }),
  'hard-landing': profile('hard-landing', 'hard-landing', {
    recoverTicks: 28, intensity: 1.08, compression: 0.38,
  }),
  'kestrel-victory': profile('kestrel-victory', 'victory', {
    enterTicks: 28, intensity: 0.96, lean: -0.06, twist: 0.14,
  }),
  'bulwark-victory': profile('bulwark-victory', 'victory', {
    enterTicks: 34, intensity: 1.08, lean: -0.1, twist: -0.08,
  }),
  'round-defeat': profile('round-defeat', 'defeat', {
    enterTicks: 26, intensity: 0.84, lean: -0.54, compression: 0.28,
  }),
  'match-defeat': profile('match-defeat', 'defeat', {
    enterTicks: 38, intensity: 1.08, lean: -1.18, compression: 0.62,
  }),
});

const STATE_PROFILE_IDS = Object.freeze({
  [S.IDLE]: 'neutral',
  [S.WALK_FWD]: 'locomotion',
  [S.WALK_BACK]: 'locomotion',
  [S.DASH_FWD]: 'locomotion',
  [S.DASH_BACK]: 'locomotion',
  [S.BACKDASH_RECOVERY]: 'locomotion-recovery',
  [S.SIDESTEP_LEFT]: 'sidestep',
  [S.SIDESTEP_RIGHT]: 'sidestep',
  [S.SIDESTEP_RECOVERY]: 'locomotion-recovery',
  [S.CROUCH_ENTER]: 'crouch',
  [S.CROUCH]: 'crouch',
  [S.CROUCH_EXIT]: 'crouch',
  [S.GUARD_STAND]: 'guard',
  [S.GUARD_CROUCH]: 'guard',
  [S.GUARD_RECOVERY]: 'guard',
  [S.PARRY_ACTIVE]: 'parry',
  [S.PARRY_SUCCESS]: 'parry',
  [S.ATTACK_STARTUP]: 'attack',
  [S.ATTACK_ACTIVE]: 'attack',
  [S.ATTACK_RECOVERY]: 'attack',
  [S.THROW_ATTEMPT]: 'throw',
  [S.THROW_HOLD]: 'throw',
  [S.THROW_EXECUTE]: 'throw',
  [S.THROW_BROKEN]: 'throw-break',
  [S.THROWN]: 'throw',
  [S.THROW_TECH]: 'throw-tech',
  [S.HITSTUN_STAND]: 'kestrel-hit-stand',
  [S.HITSTUN_CROUCH]: 'hit-crouch',
  [S.HITSTUN_LAUNCH]: 'hit-launch',
  [S.HITSTUN_AIR]: 'hit-air',
  [S.HITSTUN_WALL]: 'hit-wall',
  [S.WALL_SPLAT]: 'wall-splat',
  [S.WALL_SLUMP]: 'wall-slump',
  [S.KNOCKDOWN_FALL]: 'fall-face-up',
  [S.KNOCKDOWN_GROUND]: 'ground-face-up',
  [S.WAKEUP_NEUTRAL]: 'wakeup-neutral',
  [S.WAKEUP_ATTACK]: 'wakeup-attack',
  [S.WAKEUP_ROLL]: 'wakeup-roll',
  [S.AIRBORNE]: 'air',
  [S.AIR_ATTACK]: 'attack',
  [S.LANDING]: 'landing',
  [S.HARD_LANDING]: 'hard-landing',
  [S.STANCE_SHIFT]: 'special',
  [S.SPECIAL_CHARGE]: 'special',
  [S.FINISHER_ATTACKER]: 'special',
  [S.FINISHER_VICTIM]: 'fall-face-up',
  [S.ROUND_INTRO]: 'intro',
  [S.ROUND_WIN]: 'kestrel-victory',
  [S.ROUND_LOSS]: 'round-defeat',
  [S.MATCH_WIN]: 'kestrel-victory',
  [S.MATCH_LOSS]: 'match-defeat',
});

/**
 * Resolves one immutable state-motion profile. Identity and knockdown orientation are deliberate
 * variants, while the returned object remains shared and allocation-free.
 */
export function stateMotionFor(fighter) {
  if (!fighter) return STATE_MOTION_PROFILES.neutral;
  let id = STATE_PROFILE_IDS[fighter.state] || 'neutral';
  if (fighter.state === S.HITSTUN_STAND && fighter.id === 'bulwark') {
    id = 'bulwark-hit-stand';
  } else if (fighter.state === S.KNOCKDOWN_FALL || fighter.state === S.FINISHER_VICTIM) {
    id = fighter.faceUp ? 'fall-face-up' : 'fall-face-down';
  } else if (fighter.state === S.KNOCKDOWN_GROUND) {
    id = fighter.faceUp ? 'ground-face-up' : 'ground-face-down';
  } else if (fighter.state === S.ROUND_WIN || fighter.state === S.MATCH_WIN) {
    id = fighter.id === 'bulwark' ? 'bulwark-victory' : 'kestrel-victory';
  }
  return STATE_MOTION_PROFILES[id];
}

export function stateMotionCoverage() {
  const missing = ALL_STATES.filter((state) => {
    const id = STATE_PROFILE_IDS[state];
    return !id || !STATE_MOTION_PROFILES[id];
  });
  const families = {};
  for (const resolved of Object.values(STATE_MOTION_PROFILES)) {
    families[resolved.family] = (families[resolved.family] || 0) + 1;
  }
  return Object.freeze({
    complete: missing.length === 0,
    states: ALL_STATES.length,
    profiles: Object.keys(STATE_MOTION_PROFILES).length,
    families: Object.freeze(families),
    missing: Object.freeze(missing),
  });
}
